/* eslint-disable key-spacing */
import { SEO } from '@/constants/index'
import { getHomepageCmsAttributesApi } from '@/utils/api'
import { getAllIndexablePages, getAllRedirects, RedirectSlugObjType } from '@/utils/cmsUtils'
import { removeLastSlash } from '@/utils/general'
import { GetServerSideProps, GetServerSidePropsContext } from 'next'
import { renderToStaticMarkup } from 'react-dom/server'

declare global {
  // eslint-disable-next-line @typescript-eslint/no-namespace
  namespace JSX {
    // eslint-disable-next-line @typescript-eslint/consistent-type-definitions
    interface IntrinsicElements {
      urlset: React.DetailedHTMLProps<React.HTMLAttributes<HTMLElement>, HTMLElement> & {
        xmlns: string
      }
      url: React.DetailedHTMLProps<React.HTMLAttributes<HTMLElement>, HTMLElement>
      loc: React.DetailedHTMLProps<React.HTMLAttributes<HTMLElement>, HTMLElement>
      lastmod: React.DetailedHTMLProps<React.HTMLAttributes<HTMLElement>, HTMLElement>
    }
  }
}

type CmsSitemapEntry = {
  loc: string
  lastMod: string
}

type CmsSitemapProps = {
  pages: CmsSitemapEntry[]
}

const fanduelOrigin = new URL(SEO.FANDUEL_URL).origin

const normalizeSitemapUrl = (path?: string | null) => {
  if (!path) {
    return null
  }

  try {
    const url = new URL(path, SEO.FANDUEL_URL)

    if (url.origin !== fanduelOrigin) {
      return null
    }

    url.hash = ''
    if (url.pathname !== '/') {
      url.pathname = removeLastSlash(url.pathname)
    }

    return url.pathname === '/' && !url.search ? url.origin : url.toString()
  } catch {
    return null
  }
}

const createSitemapEntry = (path: string | null | undefined, lastMod: string): CmsSitemapEntry | null => {
  const loc = normalizeSitemapUrl(path)

  return loc ? { loc, lastMod } : null
}

const isNotNull = <T,>(value: T | null): value is T => value !== null

const toSitemapEntries = <T extends { _updatedAt?: string | null }>(
  items: T[],
  getPath: (item: T) => string | null | undefined,
  fallbackLastMod: string,
): CmsSitemapEntry[] =>
  items.map((item) => createSitemapEntry(getPath(item), item._updatedAt ?? fallbackLastMod)).filter(isNotNull)

const applyRedirects = (pages: CmsSitemapEntry[], redirects: RedirectSlugObjType[]): CmsSitemapEntry[] => {
  const redirectedPaths = new Set(redirects.map((redirect) => redirect.mk_old_path))

  return pages.filter((page) => !redirectedPaths.has(new URL(page.loc).pathname))
}

const CmsSitemap = ({ pages }: CmsSitemapProps) => {
  return (
    <urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9">
      {pages?.map((page, index) => {
        return (
          <url key={index}>
            <loc>{page.loc}</loc>
            {page.lastMod && <lastmod>{page.lastMod}</lastmod>}
          </url>
        )
      })}
    </urlset>
  )
}

export const getServerSideProps: GetServerSideProps = async ({ res }: GetServerSidePropsContext) => {
  const [resHomePageAttributes, resNewPages, resRedirects] = await Promise.all([
    getHomepageCmsAttributesApi(),
    getAllIndexablePages(),
    getAllRedirects(),
  ])
  const resHomePage = resHomePageAttributes?.allMkHomePage?.[0]
  const fallbackLastMod = new Date().toISOString()

  // Marketing pages
  const newpages = toSitemapEntries(resNewPages, (page) => page?.mk_slug?.current, fallbackLastMod)
  const pages = applyRedirects(newpages, resRedirects)

  // Fanduel.com Homepage
  const fanduelSite = {
    loc: SEO.FANDUEL_URL.slice(0, -1),
    lastMod: resHomePage._updatedAt ?? fallbackLastMod,
  }

  // ensure fanduel.com is always included
  pages.unshift(fanduelSite)

  res.setHeader('Content-Type', 'text/xml')
  res.write(renderToStaticMarkup(<CmsSitemap pages={pages} />))
  res.end()
  return {
    props: { pages },
  }
}

export default CmsSitemap
